import React from 'react';
import { Artifact } from '../types';
import { X, QrCode, Sparkles, MapPin, User, Palette, Hash, Star } from 'lucide-react';

interface ArtifactModalProps {
  artifact: Artifact | null;
  onClose: () => void;
  onOpenScanner?: () => void;
}

export const ArtifactModal: React.FC<ArtifactModalProps> = ({ artifact, onClose, onOpenScanner }) => {
  if (!artifact) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-lg animate-fade-in">
      <div 
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-gradient-to-b from-[#26132f] to-[#190c21] border-2 border-[#ffb703]/60 rounded-3xl shadow-2xl grid grid-cols-1 md:grid-cols-[320px_1fr] overflow-hidden text-[#fff8ee]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="artifact-title"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/90 text-white border-2 border-[#ffd166]/60 flex items-center justify-center transition-all active:scale-95 shadow-lg"
          aria-label="Đóng thông tin"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left column: Stamp preview */}
        <div className={`p-6 sm:p-8 flex flex-col items-center justify-center text-center bg-gradient-to-b from-[#2d1538] to-[#200e27] border-b md:border-b-0 md:border-r-2 border-[#ffb703]/30 ${!artifact.unlocked ? 'opacity-95' : ''}`}>
          <div className="relative w-48 sm:w-56 aspect-[4/5] flex items-center justify-center transition-transform duration-300 hover:scale-105 p-3 rounded-2xl bg-black/40 border-2 border-[#ffb703]/40 shadow-xl">
            <img
              src={artifact.stamp}
              alt={`Tem ${artifact.title}`}
              className={`w-full h-full object-contain filter drop-shadow-2xl ${
                !artifact.unlocked ? 'grayscale-[75%] contrast-90 brightness-90' : ''
              }`}
            />
          </div>

          <div className="flex items-center gap-1 text-[#ffd166] text-xs font-black mt-4">
            <span>⭐⭐⭐⭐⭐</span>
            <span className="text-[10px] text-[#ffd166] uppercase tracking-wider ml-1">Cực Hiếm</span>
          </div>

          <div className="mt-3 px-3.5 py-1.5 rounded-xl border text-xs font-black">
            {artifact.unlocked ? (
              <span className="text-[#06d6a0] flex items-center gap-1 justify-center">
                ✨ Đã lưu vào Balo Tem của bạn!
              </span>
            ) : (
              <span className="text-[#ff708f] flex items-center gap-1 justify-center">
                🔒 Bản xem trước · Quét QR tại bảo tàng
              </span>
            )}
          </div>
        </div>

        {/* Right column: Details */}
        <div className="p-6 sm:p-8 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-[#ffb703]/20 border border-[#ffd166]/60 text-[11px] font-black tracking-wider text-[#ffd166] uppercase">
                BỘ QUỐC BẢO · {artifact.unlocked ? '✨ ĐÃ SỞ HỮU' : '🔒 CHƯA MỞ KHÓA'}
              </span>
            </div>

            <div>
              <h2 id="artifact-title" className="text-2xl sm:text-4xl font-black text-white leading-tight tracking-tight">
                {artifact.title}
              </h2>
              <p className="text-xs sm:text-sm text-[#ffd166] font-semibold italic mt-0.5">{artifact.en}</p>
            </div>

            {/* Quick facts grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-2xl bg-[#2b1538] border border-white/10 flex items-start gap-2.5">
                <User className="w-4 h-4 text-[#ffd166] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#e0d0c5]/70 block font-semibold text-[10.5px]">Tác giả</span>
                  <strong className="text-white font-bold">{artifact.author}</strong>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-[#2b1538] border border-white/10 flex items-start gap-2.5">
                <Palette className="w-4 h-4 text-[#06d6a0] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#e0d0c5]/70 block font-semibold text-[10.5px]">Chất liệu / Thể loại</span>
                  <strong className="text-white font-bold">{artifact.material}</strong>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-[#2b1538] border border-white/10 flex items-start gap-2.5">
                <Hash className="w-4 h-4 text-[#ff708f] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#e0d0c5]/70 block font-semibold text-[10.5px]">Mã bảo vật</span>
                  <strong className="font-mono text-[#ffd166]">{artifact.code}</strong>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-[#2b1538] border border-white/10 flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#c084fc] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#e0d0c5]/70 block font-semibold text-[10.5px]">Tọa độ trưng bày</span>
                  <strong className="text-white font-bold">{artifact.location}</strong>
                </div>
              </div>
            </div>

            {/* Story */}
            <div className="p-4 rounded-2xl bg-[#1e0d26] border border-[#ffb703]/25 space-y-1.5">
              <span className="text-xs font-black uppercase tracking-wider text-[#ffd166] flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                BÍ MẬT DI SẢN & CÂU CHUYỆN LỊCH SỬ
              </span>
              <p className="text-xs sm:text-sm text-[#e8d4c5] leading-relaxed">
                {artifact.story}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
            {!artifact.unlocked && onOpenScanner && (
              <button
                onClick={() => {
                  onClose();
                  onOpenScanner();
                }}
                className="gf-button text-xs py-2.5 px-5 flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                <span>📸 Mở máy ảnh quét mã</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="gf-button gf-button-secondary text-xs py-2.5 px-5"
            >
              Đóng lại
            </button>

            <span className="text-[11px] text-[#ffd166]/70 ml-auto font-medium">
              Nguồn tư liệu: {artifact.source}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
